import { DatastoreInterface } from '@freelancer/datastore/core';
import { CurrencyCode } from '@freelancer/datastore/currencies/currencies.mock';
import { Location } from '@freelancer/datastore/project-view-users/location.model';
import { generateFreelancerReputationObjects } from '@freelancer/datastore/reputation/reputation-data.mock';
import { Skill } from '@freelancer/datastore/skills/skills.model';
import { generateUserObject } from '@freelancer/datastore/users/user.mock';
import { User } from '@freelancer/datastore/users/users.model';
import { assertNever } from '@freelancer/utils';
import { UserChosenRoleApi } from 'api-typings/users/users';
import { Badge } from './badge.model';
import { ProjectViewUser } from './project-view-users.model';
import { ProjectViewUsersCollection } from './project-view-users.types';

interface GenerateUsersOptions {
  readonly users: ReadonlyArray<User>;
  readonly currencyCode?: CurrencyCode;
}
export function createProjectViewUsers(
  datastore: DatastoreInterface,
  options: GenerateUsersOptions,
) {
  const objects = generateProjectViewUserObjects(options);
  objects.forEach(object =>
    datastore.createDocument<ProjectViewUsersCollection>(
      'projectViewUsers',
      object,
    ),
  );
  return objects;
}

function generateProjectViewUserObjects({
  users,
  currencyCode = CurrencyCode.USD,
  location,
}: GenerateUsersOptions & {
  readonly location?: Location;
}): ReadonlyArray<ProjectViewUser> {
  const reputationObjects = generateFreelancerReputationObjects({
    userIds: users.map(user => user.id),
  });

  return users.map(
    ({ id, username, displayName, role, chosenRole, status }, index) => ({
      ...generateUserObject({
        userId: id,
        username,
        displayName,
        role,
        chosenRole: chosenRole || UserChosenRoleApi.EMPLOYER,
        currencyCode,
        userStatus: status,
      }),

      badges: generateBadges(),
      jobs: generateSkills(),
      preferredFreelancer: false,
      registrationDate: Date.now(),
      qualifications: [],
      chosenRole: chosenRole || UserChosenRoleApi.EMPLOYER,
      reputation: reputationObjects[index],
      tagLine: "I'm an employer of freelancers.",
      status: status || {
        paymentVerified: true,
        emailVerified: true,
        depositMade: true,
        profileComplete: true,
        phoneVerified: true,
        identityVerified: false,
        facebookConnected: false,
      },
      location: location || generateLocation(id),
      sanctions: [],
    }),
  );
}

enum CountryCode {
  AU = 'au',
  BD = 'bd',
  MY = 'my',
  PK = 'pk',
  US = 'us',
}

function generateCountry(code: CountryCode) {
  switch (code) {
    case CountryCode.AU:
      return {
        id: 'au',
        code: 'au',
        name: 'Australia',
        flagUrl: '/img/flags/png/au.png',
      };

    case CountryCode.BD:
      return {
        id: 'bd',
        code: 'bd',
        name: 'Bangladesh',
        flagUrl: '/img/flags/png/bd.png',
      };

    case CountryCode.MY:
      return {
        id: 'my',
        code: 'my',
        name: 'Malaysia',
        flagUrl: '/img/flags/png/my.png',
      };
    case CountryCode.PK:
      return {
        id: 'pk',
        code: 'pk',
        name: 'Pakistan',
        flagUrl: '/img/flags/png/pk.png',
      };
    case CountryCode.US:
      return {
        id: 'us',
        code: 'us',
        name: 'United States',
        flagUrl: '/img/flags/png/us.png',
      };

    default:
      assertNever(code);
  }
}

function generateLocation(index: number): Location {
  const locations = [
    {
      countryCode: CountryCode.AU,
      city: 'Sydney',
      mapCoordinates: { latitude: -33.868, longitude: 151.209 },
      vicinity: 'Sydney',
    },
    {
      countryCode: CountryCode.AU,
      city: 'Melbourne',
      mapCoordinates: { latitude: -37.813, longitude: 144.963 },
      vicinity: 'Melbourne',
    },
    {
      countryCode: CountryCode.MY,
      city: 'Kuala Lumpur',
      mapCoordinates: { latitude: 3.157, longitude: 101.712 },
      vicinity: 'Kuala Lumpur',
      administrativeArea: 'Federal Territory of Kuala Lumpur',
    },
    {
      countryCode: CountryCode.BD,
      city: 'Dhaka',
      mapCoordinates: { latitude: 23.81, longitude: 90.413 },
      vicinity: 'Dhaka',
      administrativeArea: 'Dhaka Division',
    },
    {
      countryCode: CountryCode.PK,
      city: 'Karachi',
      mapCoordinates: { latitude: 24.926, longitude: 67.108 },
    },
    {
      countryCode: CountryCode.BD,
      city: 'Barishal',
      mapCoordinates: { latitude: 22.713, longitude: 90.353 },
    },
    {
      countryCode: CountryCode.US,
      city: 'Oakland',
      mapCoordinates: { latitude: 37.775, longitude: -122.419 },
      vicinity: 'San Francisco',
      administrativeArea: 'California',
    },
  ].map(
    ({ countryCode, city, mapCoordinates, vicinity, administrativeArea }) => ({
      country: generateCountry(countryCode),
      city,
      mapCoordinates,
      vicinity,
      administrativeArea,
    }),
  );

  return locations[index % locations.length];
}

function generateBadges(): ReadonlyArray<Badge> {
  return [
    {
      id: 1,
      name: 'The Prospector',
      description: 'Post your first project.',
      timeAwarded: 1481700485652000,
      iconUrl: '/img/badges/badge_1.png',
    },
    {
      id: 2,
      name: 'Entrepreneur',
      description: 'Post 50 projects.',
      timeAwarded: 1559809160278000,
      iconUrl: '/img/badges/badge_2.png',
    },
    {
      id: 4,
      name: 'Nine-to-Fiver ',
      description: 'Win a project and accept the offer.',
      timeAwarded: 1484182114224000,
      iconUrl: '/img/badges/badge_4.png',
    },
    {
      id: 8,
      name: 'The Giver',
      description: 'Award your project to a Freelancer.',
      timeAwarded: 1487760543899000,
      iconUrl: '/img/badges/badge_8.png',
    },
    {
      id: 9,
      name: 'The Rainmaker',
      description: 'Award 50 projects.',
      timeAwarded: 1561004447766000,
      iconUrl: '/img/badges/badge_9.png',
    },
    {
      id: 13,
      name: 'The Adventurer',
      description: 'Complete your first project.',
      timeAwarded: 1481855462140000,
      iconUrl: '/img/badges/badge_13.png',
    },
    {
      id: 16,
      name: 'The Seeker',
      description: 'Bid on 20 projects.',
      timeAwarded: 1524706845691000,
      iconUrl: '/img/badges/badge_16.png',
    },
    {
      id: 21,
      name: 'The Speculator',
      description: 'Sponsor 1 bid.',
      timeAwarded: 1527676455930000,
      iconUrl: '/img/badges/badge_21.png',
    },
    {
      id: 27,
      name: 'Smart Money',
      description: 'Create 10 Milestones. ',
      timeAwarded: 1521076752971000,
      iconUrl: '/img/badges/badge_27.png',
    },
    {
      id: 28,
      name: 'Mover and Shaker',
      description: 'Create 50 Milestones.',
      timeAwarded: 1526876404272000,
      iconUrl: '/img/badges/badge_28.png',
    },
    {
      id: 31,
      name: 'All Star',
      description: 'Receive a 5-star rating for a project.',
      timeAwarded: 1519105238585000,
      iconUrl: '/img/badges/badge_31.png',
    },
    {
      id: 37,
      name: 'High Roller',
      description:
        'Complete a project worth over $500USD (or equivalent) on Freelancer.com.',
      timeAwarded: 1519104610971000,
      iconUrl: '/img/badges/badge_37.png',
    },
    {
      id: 38,
      name: 'Warren Buffet',
      description: 'Complete a project worth over $1000 USD.',
      timeAwarded: 1527123533046000,
      iconUrl: '/img/badges/badge_38.png',
    },
    {
      id: 45,
      name: 'Full Service',
      description:
        'Successfully complete a contest, then use the design to complete a project.',
      timeAwarded: 1549518941897000,
      iconUrl: '/img/badges/badge_45.png',
    },
    {
      id: 50,
      name: 'The Perfectionist',
      description: 'Edited a bid.',
      timeAwarded: 1474565192666000,
      iconUrl: '/img/badges/badge_50.png',
    },
    {
      id: 55,
      name: 'The Collector',
      description: 'Reach a balance of 5,000 credits.',
      timeAwarded: 1481700486965000,
      iconUrl: '/img/badges/badge_55.png',
    },
    {
      id: 63,
      name: 'The Deal-Closer',
      description: 'Get awarded 95% of projects (for over 5 projects)',
      timeAwarded: 1564018710420000,
      iconUrl: '/img/badges/badge_63.png',
    },
    {
      id: 66,
      name: 'The Regular',
      description: 'Log in every day for 1 week.',
      timeAwarded: 1496817208353000,
      iconUrl: '/img/badges/badge_66.png',
    },
    {
      id: 67,
      name: 'Frequent Visitor',
      description: 'Log in in every day for 1 month.',
      timeAwarded: 1506410827134000,
      iconUrl: '/img/badges/badge_67.png',
    },
    {
      id: 69,
      name: 'Contest Host',
      description: 'Post a contest.',
      timeAwarded: 1527733001258000,
      iconUrl: '/img/badges/badge_69.png',
    },
    {
      id: 75,
      name: 'The Selector',
      description: 'Award 1 contest.',
      timeAwarded: 1527733001555000,
      iconUrl: '/img/badges/badge_75.png',
    },
    {
      id: 78,
      name: 'Punch it Live!',
      description: 'Post a Priority Project.',
      timeAwarded: 1527831275660000,
      iconUrl: '/img/badges/badge_78.png',
    },
    {
      id: 81,
      name: 'The Investor',
      description: 'Post a featured project.',
      timeAwarded: 1481700486152000,
      iconUrl: '/img/badges/badge_81.png',
    },
    {
      id: 88,
      name: 'Insta-Hire',
      description: 'Use Hire Me to employ a Freelancer.',
      timeAwarded: 1527733001960000,
      iconUrl: '/img/badges/badge_88.png',
    },
    {
      id: 91,
      name: 'Fast Responder',
      description: 'Bid within 2 minutes of a project being listed.',
      timeAwarded: 1484182192954000,
      iconUrl: '/img/badges/badge_91.png',
    },
    {
      id: 93,
      name: 'The Verified',
      description: 'Verify your payment method',
      timeAwarded: 1481700486653000,
      iconUrl: '/img/badges/badge_93.png',
    },
    {
      id: 94,
      name: 'Backup plan!',
      description: 'Verify a backup payment method',
      timeAwarded: 1559096645522000,
      iconUrl: '/img/badges/badge_94.png',
    },
    {
      id: 95,
      name: 'Quick Pick',
      description: 'Award your project within 24 hours of posting',
      timeAwarded: 1517293840485000,
      iconUrl: '/img/badges/badge_95.png',
    },
    {
      id: 96,
      name: 'Speedy Hire',
      description: 'Award 15 projects within 24 hours of posting',
      timeAwarded: 1559114843750000,
      iconUrl: '/img/badges/badge_96.png',
    },
    {
      id: 97,
      name: 'Lightning Employment',
      description: 'Award 30 projects within 24 hours of posting',
      timeAwarded: 1559190728466000,
      iconUrl: '/img/badges/badge_97.png',
    },
  ];
}

export function generateSkills(): ReadonlyArray<Skill> {
  return [
    {
      id: 3,
      name: 'PHP',
      category: { id: 1, name: 'Websites, IT & Software' },
      local: false,
      seoUrl: 'php',
      activeProjectCount: 0,
    },
    {
      id: 20,
      name: 'Graphic Design',
      category: { id: 3, name: 'Design, Media & Architecture' },
      local: false,
      seoUrl: 'graphic-design',
      activeProjectCount: 0,
    },
  ];
}
